import { Icon } from "@iconify/react";
import { Menu, Layout, Typography, Divider, MenuProps, Button, Popover, Space } from "antd";
import styles from "./Navbar.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactElement, useEffect, useState } from "react";
import { useHasAccess } from "~/Hooks/useHasAccess";
import moment from "moment";
import { isObject } from "lodash";
import { enabledMenuItems, orgName, UserType } from "../../Utils/Constant";
import { useAuth } from "@hooks/useAuth";
import { useHasPanelAccess } from "../../Hooks/useHasAccess";
import { PANEL_ROLES } from "../../Routes/Routes";
const { Footer } = Layout;
const { Text, Title } = Typography;

const getIcon = (icon: string, rotate = 0) => (
  <Icon
    icon={icon}
    className={styles.navbarItemIcon}
    inline
    rotate={rotate}
  />
);

type NavItem = {
  icon: ReactElement;
  label: string;
  key: string;
  locked?: boolean;
  roles: string[];
};

type AntMenuItem = Required<MenuProps>["items"][number];

interface MenuItem extends NavItem {
  childrens?: NavItem[];
}

interface Props {
  collapsed: boolean;
}

export const Navbar = ({ collapsed }: Props) => {
  const { hasPermissions, hasRole } = useHasAccess();
  /* Panel roles are separate from the ePOD roles `hasRole` reads. */
  const { hasPanelRole } = useHasPanelAccess();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  const navigate = useNavigate();

  function getItem({
    roles,
    label,
    key,
    icon,
    children,
    locked,
    checkRole = true,
  }: {
    roles: string[];
    label: React.ReactNode;
    key?: React.Key | null;
    icon?: React.ReactNode;
    children?: AntMenuItem[];
    locked?: boolean;
    checkRole?: boolean;
  }): AntMenuItem {
    if (checkRole) {
      if (hasRole(roles)) {
        return {
          key,
          icon,
          children,
          label,
          ...(locked && { disabled: true }),
        } as AntMenuItem;
      } else {
        return null;
      }
    } else if (hasRole(roles)) {
      return {
        key,
        icon,
        children,
        label,
        ...(isObject(label?.valueOf()) && {
          className: styles.PopoverMenu,
        }),
        ...(locked && !isObject(label?.valueOf()) && { disabled: true }),
      } as AntMenuItem;
    }
    return null;
  }

  /* The menu has to agree with the route guards in Routes.tsx — an item that
     leads to the 403 page is worse than no item at all. */
  const ADMIN_ONLY = [PANEL_ROLES.ADMIN, "PANEL_MASTER_ADMIN"];
  const APPROVES = [...ADMIN_ONLY, PANEL_ROLES.REVIEWER, "PANEL_MASTER_REVIEWER"];

  const panelItem = (allowed: string[], item: AntMenuItem) => (hasPanelRole(allowed) ? item : null);

  const primaryItems: AntMenuItem[] = [
    getItem({
      roles: ["*"],
      label: "Work Order Dashboard",
      key: `/workorder-dashboard`,
      icon: getIcon("ic:outline-poll"),
    }),
    getItem({
      roles: ["*"],
      label: "Work Order",
      key: `/workorder`,
      icon: getIcon("mdi:clipboard-text-outline"),
    }),
    panelItem(
      APPROVES,
      getItem({
        roles: ["*"],
        label: "Work Order Approval",
        key: `/workorder-approval?activeTab=pendingApproval`,
        icon: getIcon("mdi:clipboard-check-outline"),
      })
    ),
    panelItem(
      ADMIN_ONLY,
      getItem({
        roles: ["*"],
        label: "Manage Catalog",
        key: `/workorder-manage-catalog?activeTab=template`,
        icon: getIcon("fluent:document-checkmark-20-regular"),
      })
    ),
    panelItem(
      ADMIN_ONLY,
      getItem({
        roles: ["*"],
        label: "Approval Management",
        key: `/approval-management?activeTab=subsidiaryDivision`,
        icon: getIcon("mdi:account-check-outline"),
      })
    ),
    panelItem(
      ADMIN_ONLY,
      getItem({
        roles: ["*"],
        label: "Work Order User Management",
        key: `/workorder-user-management?activeTab=activeUsers`,
        icon: getIcon("mdi:contract-sign"),
      })
    ),
    // getItem({
    //   roles: ["*"],
    //   label: "SPA",
    //   key: `/spa`,
    //   icon: getIcon("mdi:contract-sign"),
    // }),
    // getItem({
    //   roles: ["SPA_ADMIN","SPA_APPROVER"],
    //   label: "SPA Approvals",
    //   key: `/spa-review?activeTab=pendingReview`,
    //   icon: getIcon("mdi:contract-sign"),
    // }),
    // getItem({
    //   roles: ["*"],
    //   label: "SPA Watchers",
    //   key: `/spa-watcher?watchers=true`,
    //   icon: getIcon("mdi:contract-sign"),
    // }),
    // getItem({
    //   roles: ["SPA_ADMIN"],
    //   label: "Subsidiary/Division",
    //   key: `/subsidiaries`,
    //   icon: getIcon("fluent:document-checkmark-20-regular"),
    // }),
    // getItem({
    //   roles: ["SPA_ADMIN"],
    //   label: "Cost Centre ",
    //   key: `/divisions`,
    //   icon: getIcon("fluent:document-checkmark-20-regular"),
    // }),
    // getItem({
    //   roles: ["SPA_ADMIN"],
    //   label: "Approvers",
    //   key: `/approver?authority=md/gceo`,
    //   icon: getIcon("mdi:contract-sign"),
    // }),
    // getItem({
    //   roles: ["SPA_ADMIN"],
    //   label: "Budget Manager",
    //   key: `/budget-manager`,
    //   icon: getIcon("mdi:contract-sign"),
    // }),
    // getItem({
    //   roles:  ["SPA_ADMIN"],
    //   label: "Secretariat, PCD",
    //   key: `/one-stop-centre`,
    //   icon: getIcon("mdi:contract-sign"),
    // }),
    // getItem({
    //   roles: ["SPA_ADMIN"],
    //   label: "PCD Watchers",
    //   key: `/pcd-watchers`,
    //   icon: getIcon("mdi:contract-sign"),
    // }),
    // getItem({
    //   roles:  ["SPA_ADMIN"],
    //   label: "Logs",
    //   key: `/logs?activeTab=document`,
    //   icon: getIcon("mdi:contract-sign"),
    // }),
  ];

  const secondaryItems: AntMenuItem[] = [];

  const getOpenKeys = () => {
    const keys: any = [];

    primaryItems.find((o: any) =>
      o?.key === pathname
        ? keys.push(o.key)
        : o?.children?.find((c: any) => (c?.key === pathname ? keys.push(o.key) : null))
    );

    return keys;
  };

  const getPrimaryActiveKey = () => {
    let result = "";
    primaryItems.map((primary: any) => {
      primary?.key?.split("/")[1]?.split("?")[0] === pathname.split("/")[1]
        ? (result = primary.key)
        : primary?.children?.map((child: any) => {
            child?.key?.split("/")[2]?.split("?")[0] === pathname.split("/")[2]?.split("?")[0]
              ? (result = child?.key)
              : "";
          });
    });

    return result;
  };
  const getSecondaryActiveKey = () => {
    let result = "";
    secondaryItems.map((secondary: any) => {
      secondary?.key.split("/")[1]?.split("?")[0] === pathname.split("/")[1]
        ? (result = secondary.key)
        : secondary?.children?.map((child: any) => (child.key === pathname ? (result = child.key) : ""));
    });
    return result;
  };

  return (
    <Layout
      style={{
        background: styles.primary,
        height: "100%",
        paddingTop: 12,
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <Menu
        selectable
        mode="inline"
        items={primaryItems.filter((item: any) => item && enabledMenuItems.includes(item.label))}
        style={{
          background: styles.primary,
        }}
        className={styles.navBar}
        defaultOpenKeys={getOpenKeys()}
        selectedKeys={[getPrimaryActiveKey()]}
        onClick={({ key }) => {
          if (key === "nocloud") {
            // setOpen(true);
            return false;
          } else {
            navigate(key);
          }
        }}
      />
      <div>
        <span className={styles.divider}>
          <Divider style={{ marginBlock: styles.whitespace3 }} />
        </span>
        {!collapsed && (
          <Footer className={styles.footerStyle}>
            <Title
              level={5}
              style={{ color: styles.white }}
            >
              &copy; {orgName} {moment().format("YYYY")}
            </Title>
          </Footer>
        )}
      </div>
    </Layout>
  );
};
